import { z } from 'zod/v4';
// Holiday type
export const HolidayTypeSchema = z.enum([
    'federal',
    'state',
    'company',
    'court-specific',
]);
// Holiday schema
export const HolidaySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    date: z.string(), // ISO date (YYYY-MM-DD)
    holidayType: HolidayTypeSchema,
    state: z.string().optional(), // For state holidays
    courtCode: z.string().optional(), // For court-specific closures
    recurring: z.boolean().default(false), // Repeats annually
    notes: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateHolidaySchema = HolidaySchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateHolidaySchema = HolidaySchema.partial().omit({
    id: true,
    createdAt: true,
});
// QuickBooks connection status
export const QuickBooksConnectionStatusSchema = z.enum([
    'connected',
    'disconnected',
    'error',
    'pending',
]);
// QuickBooks settings (placeholder)
export const QuickBooksSettingsSchema = z.object({
    connectionStatus: QuickBooksConnectionStatusSchema.default('disconnected'),
    companyName: z.string().optional(),
    companyId: z.string().optional(),
    realmId: z.string().optional(),
    lastSyncAt: z.string().datetime().optional(),
    autoSync: z.boolean().default(false),
    syncFrequency: z.enum(['realtime', 'hourly', 'daily']).default('daily'),
    syncInvoices: z.boolean().default(true),
    syncPayments: z.boolean().default(true),
    syncCustomers: z.boolean().default(true),
    defaultIncomeAccount: z.string().optional(),
    defaultExpenseAccount: z.string().optional(),
});
// System settings - general configuration
export const SystemSettingsSchema = z.object({
    id: z.string().uuid(),
    // Company information
    companyName: z.string(),
    companyLegalName: z.string().optional(),
    companyAddress: z.object({
        street1: z.string(),
        street2: z.string().optional(),
        city: z.string(),
        state: z.string(),
        zip: z.string(),
        country: z.string().default('US'),
    }).optional(),
    companyPhone: z.string().optional(),
    companyFax: z.string().optional(),
    companyEmail: z.string().email().optional(),
    companyWebsite: z.string().url().optional(),
    // Tax information
    taxId: z.string().optional(), // EIN
    stateTaxId: z.string().optional(),
    // Default messages
    orderConfirmationMessage: z.string().optional(),
    orderCompletionMessage: z.string().optional(),
    invoiceMessage: z.string().optional(),
    receiptMessage: z.string().optional(),
    // Fee settings
    defaultProcessingFeePercent: z.number().min(0).max(100).default(0),
    creditCardFeePercent: z.number().min(0).max(100).default(2.9),
    achFeeFlat: z.number().default(0),
    rushFeeMultiplier: z.number().default(1.5),
    sameDayFeeMultiplier: z.number().default(2.0),
    // SLA defaults (in hours)
    defaultSlaStandard: z.number().default(24),
    defaultSlaPriority: z.number().default(8),
    defaultSlaUrgent: z.number().default(4),
    // Business hours
    businessHoursStart: z.string().default('09:00'),
    businessHoursEnd: z.string().default('17:00'),
    businessTimezone: z.string().default('America/Los_Angeles'),
    businessDays: z.array(z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']))
        .default(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']),
    // Notification settings
    emailFromAddress: z.string().email().optional(),
    emailFromName: z.string().optional(),
    smsEnabled: z.boolean().default(false),
    smsFromNumber: z.string().optional(),
    // Integration settings (just flags, actual config elsewhere)
    quickbooksEnabled: z.boolean().default(false),
    auth0Enabled: z.boolean().default(true),
    // QuickBooks settings (placeholder)
    quickbooks: QuickBooksSettingsSchema.optional(),
    // Feature flags
    retainerEnabled: z.boolean().default(true),
    preAuthEnabled: z.boolean().default(true),
    efilingEnabled: z.boolean().default(true),
    eserviceEnabled: z.boolean().default(true),
    vendorDispatchEnabled: z.boolean().default(true),
    // Timestamps
    updatedAt: z.string().datetime(),
    updatedBy: z.string().optional(),
});
export const UpdateSystemSettingsSchema = SystemSettingsSchema.partial().omit({
    id: true,
});
